import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertTriangle, IconPlus } from "@tabler/icons-react";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { ZodIssue } from "zod";
import { AG_GRID_LOCALE_NOR } from "../../../agGridLocale";
import { McInput } from "../../../components";
import TemplateSelector, { ITemplate } from "../../../components/TemplateSelector/TemplateSelector";
import { ZodErrorModal } from "../../../components/ZodErrorModal/ZodErrorModal";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { MCApplicationsUrlEnum } from "../../../mc-shared/sharedConstants";
import { formatShortDate } from "../admin/selectors";
import { doCreateReportBoard } from "./reportBoardAPI";
import {
  REPORT_BOARD_STATUS_COLOR,
  REPORT_BOARD_STATUS_CONSTANTS,
  REPORT_BOARD_STATUSES,
  ReportBoardStatusEnums,
} from "./reportBoardConstants";
import { useGetReportBoardsQuery } from "./reportRTK";
import { fetchAndSetReportTemplatesThunk, resetReportApp } from "./reportSlice";

const ReportApp: React.FC = () => {
  const [modalIsOpen, setModalOpen] = useState<boolean>(false);
  const [activeStatus, setActiveStatus] = useState<ReportBoardStatusEnums>(ReportBoardStatusEnums.ACTIVE);

  const [zodErros, setZodErrors] = useState<ZodIssue[]>(null);
  const [selectedTitle, setSelectedTitle] = useState<string>("");
  const [filterReportsByLoggedInUserChecked, setFilterReportsByLoggedInUserChecked] = useState<boolean>(false);

  const project = useAppSelector((state) => state.adminReducer.project);
  const reportTemplates = useAppSelector((state) => state.reportReducer.reportTemplates);
  const user = useAppSelector((state) => state.globalReducer.user);

  const { data: _reports = [] } = useGetReportBoardsQuery(
    {
      projectId: project._id,
    },
    {
      skip: !project._id,
      refetchOnMountOrArgChange: true,
    },
  );

  const dispatch = useAppDispatch();

  const init = useCallback(async () => {
    dispatch(resetReportApp());
    dispatch(fetchAndSetReportTemplatesThunk());
  }, [dispatch]);

  React.useEffect(() => {
    init();
  }, [init]);

  const statuses = REPORT_BOARD_STATUSES;

  const reportBoardsFiltred = useMemo(() => {
    if (filterReportsByLoggedInUserChecked !== true) {
      return _reports;
    }

    return _reports?.filter((report) => report.createdBy?._id === user._id) ?? [];
  }, [_reports, filterReportsByLoggedInUserChecked, user._id]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    reportBoardsFiltred.forEach((board) => {
      counts[board.status] = (counts[board.status] || 0) + 1;
    });
    return counts;
  }, [reportBoardsFiltred]);

  const convertedReportTemplates: ITemplate[] = useMemo(
    () =>
      reportTemplates
        ?.filter((template) => !String(template.templateId ?? "").startsWith("9"))
        .map((template) => {
          return {
            _id: template.templateId.toString(),
            name: template.name,
            templateType: "MC",
            description: template.description,
            admin: template.admin,
            category: null,
          };
        }) ?? [],
    [reportTemplates],
  );

  const defaultColDef = useMemo(
    () => ({
      minWidth: 120,
      resizable: true,
      suppressHeaderMenuButton: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
        wordBreak: "break-word",
      },
    }),
    [],
  );

  const history = useHistory();

  const goToReportBoard = useCallback(
    async (boardId: string) => {
      history.push(`/project/${project._id}/reports/${boardId}`);
    },
    [history, project._id],
  );

  const openZodErrors = useCallback((errors: ZodIssue[]) => {
    setZodErrors(errors);
  }, []);

  const columns: ColDef<any>[] = useMemo(() => {
    const cols: ColDef<any>[] = [
      {
        field: "status",
        headerName: "Status",
        filter: true,
        cellRenderer: (params) => (
          <Group gap={8} wrap="nowrap">
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: REPORT_BOARD_STATUS_COLOR[params.data.status],
              }}
            />
            <Text size="xs" fw={600} c="#58595B">
              {REPORT_BOARD_STATUS_CONSTANTS[params.data.status]?.toUpperCase()}
            </Text>
          </Group>
        ),
      },
      {
        field: "name",
        headerName: "Tittel",
        flex: 1,
        filter: "agTextColumnFilter",
        cellRenderer: (params) => {
          if (!params.data) return null;
          return (
            <Anchor fw={500} onClick={() => goToReportBoard(params.data._id)}>
              {String(params.value ?? "").toUpperCase()}
            </Anchor>
          );
        },
      },
      {
        field: "createdAt",
        width: 130,
        headerName: "Opprettet",
        initialSort: "desc",
        cellRenderer: (params) => formatShortDate(params.value),
        filter: "agDateColumnFilter",
      },
      {
        field: "templateName",
        headerName: "Mal brukt",
        filter: "agTextColumnFilter",
        cellRenderer: (params) => <div className="d-flex align-items-center truncateHeaderText">{params.value}</div>,
      },
      {
        field: "createdBy.name",
        headerName: "Ansvarlig",
        filter: "agTextColumnFilter",
      },
      {
        field: "zodErrors",
        headerName: "Feil",
        width: 120,
        cellRenderer: (params) => {
          return (
            <>
              {params.value != null && (
                <Button
                  variant="light"
                  color="orange"
                  leftSection={<IconAlertTriangle size={16} />}
                  onClick={() => openZodErrors(params.value)}
                >
                  {params.value?.length}
                </Button>
              )}
            </>
          );
        },
      },
    ];
    return cols;
  }, [goToReportBoard, openZodErrors]);

  const createNew = useCallback(
    async (templateId: number) => {
      const createdReportBoard = await doCreateReportBoard(project._id, selectedTitle, templateId);
      setModalOpen(false);
      goToReportBoard(createdReportBoard._id);
    },
    [goToReportBoard, project._id, selectedTitle],
  );

  const toggle = useCallback(() => {
    setSelectedTitle("");
    setModalOpen(!modalIsOpen);
  }, [modalIsOpen]);

  return (
    <Stack gap="xs" h="100%" w="100%" p="sm">
      {zodErros != null && <ZodErrorModal errors={zodErros} onAbort={() => setZodErrors(null)} />}
      <Group gap="sm" align="center">
        <Button leftSection={<IconPlus />} size="sm" onClick={() => setModalOpen(true)}>
          Ny rapport
        </Button>
        <SegmentedControl
          value={activeStatus}
          onChange={(value) => setActiveStatus(value as ReportBoardStatusEnums)}
          color="mc-primary"
          size="md"
          styles={{ label: { overflow: "visible" } }}
          data={statuses.map((status) => {
            const count = statusCounts[status.value] || 0;
            const isActive = activeStatus === status.value;
            return {
              value: status.value,
              label: (
                <Center inline style={{ gap: 10, whiteSpace: "nowrap" }} px="sm">
                  <span>{status.displayValue}</span>
                  <Badge
                    h="20px"
                    miw={28}
                    variant={isActive ? "filled" : "outline"}
                    color={isActive ? "blue" : "gray"}
                    radius="sm"
                    fz={11}
                  >
                    <span>{count}</span>
                  </Badge>
                </Center>
              ),
            };
          })}
        />
        <Box style={{ flex: 1 }} />
        <Checkbox
          style={{ marginBottom: "-8px" }}
          checked={filterReportsByLoggedInUserChecked === true}
          onChange={(event) => setFilterReportsByLoggedInUserChecked(event.currentTarget.checked)}
          label="Vis kun rapporter opprettet av meg"
        />
      </Group>
      <div className="checklistTable ag-theme-quartz h-100 w-100">
        <AgGridReact
          rowData={reportBoardsFiltred?.filter((board) => board.status === activeStatus)}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          suppressClickEdit={true}
          rowSelection={{ mode: "singleRow", checkboxes: false }}
          animateRows={true}
          suppressContextMenu={true}
          localeText={AG_GRID_LOCALE_NOR}
        />
      </div>
      {reportTemplates != null && modalIsOpen === true && (
        <TemplateSelector
          templates={convertedReportTemplates}
          header={"Ny rapport"}
          app={{
            name: MCApplicationsUrlEnum.REPORT,
            icon: "appIcons/reportGenerator.svg",
            iconColor: "#6791a1",
          }}
          onCreate={(template) => {
            createNew(parseFloat(template._id));
          }}
          closeModal={toggle}
          isTitleEmpty={selectedTitle === ""}
          isTemplateProject={false}
          descriptionAndAdmin={true}
          showMCButton={false}
          showProjectButton={false}
          showInstanceButton={false}
          preSelectedTemplate={convertedReportTemplates[0]}
          disableOpprettButton={selectedTitle === ""}
          excelImport={{
            enabled: false,
          }}
        >
          <Stack gap="md" px="md" pt="md" style={{ background: "#FBFBFB" }}>
            <Text ta="center" fw={700} size="lg">
              NY RAPPORT
            </Text>
            <div className="grid-container">
              <Text fw={600}>Navn*</Text>
              <McInput
                required
                autoFocus
                onChange={(t) => setSelectedTitle(t.target.value)}
                value={selectedTitle}
                placeholder={"Angi navnet på rapporten"}
              />
            </div>
          </Stack>
        </TemplateSelector>
      )}
    </Stack>
  );
};

export default ReportApp;
